import {
  GatewayContact,
  GatewayDeclarationActivite,
  GatewayEntreprise,
} from "@conformite/gateway";
import {
  Button,
  displayErrorToast,
  Link,
  MaxWidthContent,
  SkeletonCard,
} from "@endya-conformite/ui-kit";
import { StoreEntryFetchStatus } from "@src/store/store";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { MainContentPageTemplate } from "@src/components/MainPageTemplate/MainContentPageTemplate";
import ChevronLeft from "@endya-conformite/ui-kit/src/assets/icons/chevron-left.svg?react";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";
import { useMillesime } from "@src/store/store.millesime";
import { GatewayDeclarationActiviteApi } from "@src/api/conformite/declarationActivite.api";
import { useEntreprises } from "@src/store/store.entreprise";
import {
  useHasForbiddenTypeAdhesion,
  useIsAllowed,
} from "@src/session/useIsAllowed";
import styles from "./ConformiteDeclarationActivite.module.scss";
import { ConformiteDeclarationActiviteIasRead } from "./read/ConformiteDeclarationActiviteIasRead";
import { ConformiteDeclarationActiviteIasEdit } from "./edit/ConformiteDeclarationActiviteIasEdit";

export function ConformiteDeclarationActiviteIas() {
  const { id } = useParams<{
    id: string;
  }>();
  const hasForbiddenTypeAdhesion = useHasForbiddenTypeAdhesion();
  const [query] = useSearchParams();
  const { hasAllowedRole } = useIsAllowed();
  const fromAxe = query.get("fromAxe");
  const { entreprisesBySiren, updateAvancement, status } = useEntreprises();
  const [isEditing, setIsEditing] = useState(false);
  const { selected: millesime } = useMillesime();
  const societe = id && entreprisesBySiren ? entreprisesBySiren[id] : undefined;
  const [declarationActivite, setDeclarationActivite] =
    useState<GatewayDeclarationActivite.DeclarationActiviteIas | null>();
  useEffect(() => {
    if (!id || !millesime) return;
    GatewayDeclarationActiviteApi.getIAS(id, millesime)
      .then((data) => {
        setDeclarationActivite(data);
      })
      .catch((error) => {
        handleGatewayError({
          onUnhandled: () => {
            displayErrorToast(
              "Une erreur est survenue lors de la récupération du formulaire"
            );
          },
        })(error);
      });
  }, [id, millesime]);

  if (!id) {
    return <Navigate to="/societes" />;
  }

  const isConforme = societe?.avancement.declarationActiviteIas === 100;

  const isOptional = societe?.declarationActiviteIASOptionnelle ?? false;
  return (
    <MainContentPageTemplate
      title={
        <div className={styles.title}>
          <h1>Déclaration Activité IAS</h1>
          <h2>{societe?.raisonSociale}</h2>
        </div>
      }
      hasMillesimeNavigation={hasAllowedRole(
        GatewayContact.ContactRoleUtilisateur.ADMINISTRATEUR,
        GatewayContact.ContactRoleUtilisateur.RESPONSABLE_CONFORMITE
      )}
      actionNavigation={
        hasForbiddenTypeAdhesion([
          GatewayEntreprise.TypeAdhesion.SOCIETE_INDIVIDUELLE,
        ]) ? null : (
          <Link
            to={
              fromAxe === null
                ? `/societes/${id}`
                : "/conformite/declaration-activite/ias"
            }
          >
            <Button variant="text" icon={<ChevronLeft />}>
              Retour
            </Button>
          </Link>
        )
      }
      noEntity={!societe && status === StoreEntryFetchStatus.Loaded}
      millesime={millesime}
    >
      {status === StoreEntryFetchStatus.Loading && (
        <MaxWidthContent size="large">
          <SkeletonCard height={500} />
        </MaxWidthContent>
      )}
      {millesime &&
        societe &&
        declarationActivite !== undefined &&
        !isEditing && (
          <ConformiteDeclarationActiviteIasRead
            declarationActivite={declarationActivite}
            isConforme={isConforme}
            setToEdit={() => setIsEditing(true)}
            millesime={millesime}
            isOptional={isOptional}
          />
        )}
      {millesime &&
        societe &&
        declarationActivite !== undefined &&
        isEditing && (
          <ConformiteDeclarationActiviteIasEdit
            siren={societe.siren}
            millesime={millesime}
            declarationActivite={declarationActivite}
            isConforme={isConforme}
            stopEditing={(value, avancement) => {
              setIsEditing(false);
              if (value) setDeclarationActivite(value);
              if (avancement) updateAvancement(avancement);
            }}
          />
        )}
    </MainContentPageTemplate>
  );
}
